import {
  Table,
  type Column,
  type Row,
  type TableEmptyStateLabels,
} from '@ardoq/table';
import { GremlinResult } from '../types';
import styled from 'styled-components';
import { formatValue } from '../utils';
import { t, type I18nLocale } from '@ardoq/i18n';
import { type Locale } from '@ardoq/locale';

type GremlinResultsTableDirectProps = {
  results: GremlinResult[];
  emptyStateLabels: TableEmptyStateLabels;
  locale: Locale;
  i18nLocale: I18nLocale;
};

const PrettyStyleBase = styled.div`
  display: block;
  font-size: 13px;
  word-break: break-all;
  overflow-wrap: anywhere;
  font-family: monospace;
  padding: 0;
  margin: 0;
  color: #333333;
  background-color: transparent;
  border: none;
  line-height: 1.4;
`;

const PrettyRender = ({ value }: { value: unknown }) => {
  const prettyString = JSON.stringify(value, null, 2);
  return (
    <PrettyStyleBase as={prettyString.length > 3000 ? 'span' : 'pre'}>
      {prettyString}
    </PrettyStyleBase>
  );
};

type RowData = {
  result: { value: unknown };
};

type ResultCellProps = RowData['result'] & { locale: Locale };

const ResultCell = ({ value, locale }: ResultCellProps) => {
  if (typeof value === 'object' && value !== null) {
    return <PrettyRender value={value} />;
  }
  if (typeof value === 'string' || typeof value === 'boolean') {
    return formatValue(value, locale);
  }
  return String(value);
};

const getResultValue = ({ value }: RowData['result']): string =>
  typeof value === 'object' && value !== null
    ? JSON.stringify(value)
    : String(value);

const getColumns = (
  locale: Locale,
  i18nLocale: I18nLocale
): Column<RowData>[] => [
  {
    title: t(i18nLocale, 'search:gremlinResultsTable.directResults', 'Results'),
    id: 'result',
    cellRenderer: cellData => <ResultCell {...cellData} locale={locale} />,
    valueGetter: getResultValue,
    autoHeight: true,
  },
];

const toRows = (results: GremlinResult[]): Row<RowData>[] =>
  results.map((result, index) => ({
    id: String(index),
    data: { result: { value: result } },
  }));

const GremlinResultsTableDirect = ({
  results,
  emptyStateLabels,
  locale,
  i18nLocale,
}: GremlinResultsTableDirectProps) => (
  <Table<RowData>
    aria-label={t(
      i18nLocale,
      'search:gremlinResultsTable.directAriaLabel',
      'Gremlin direct results'
    )}
    rows={toRows(results)}
    emptyStateLabels={emptyStateLabels}
    columns={getColumns(locale, i18nLocale)}
    sortable={{ descriptor: null }}
    height="calc(100vh - 410px)"
  />
);

export { GremlinResultsTableDirect };
